import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import StorefrontIcon from '@mui/icons-material/Storefront';
import NotchTabs from '../../../components/common/NotchTabs';

export const HALL_ACCENT = '#4ce1b6';
export const HALL_TAB_PATH_PREFIX = '/hall/';

export const HALL_TAB_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', shortLabel: 'Dash', icon: DashboardIcon },
  { id: 'booking-calendar', label: 'Booking Calendar', shortLabel: 'Calendar', icon: CalendarMonthIcon },
  { id: 'list-events', label: 'List Events', shortLabel: 'Events', icon: EventIcon },
  { id: 'hall-management', label: 'Hall Management', shortLabel: 'Halls', icon: MapsHomeWorkIcon },
  { id: 'vendor-management', label: 'Vendor Management', shortLabel: 'Vendors', icon: StorefrontIcon },
];

const TAB_ID_TO_INDEX = HALL_TAB_ITEMS.reduce((acc, item, index) => {
  acc[item.id] = index;
  return acc;
}, {});

export function getHallTabPath(tabId = 'dashboard') {
  return `${HALL_TAB_PATH_PREFIX}${tabId}/`;
}

export function getHallTabIndexFromLocation(pathname = '', search = '') {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/hall';

  if (normalizedPath === '/hall') {
    const tabId = new URLSearchParams(search).get('tab');
    if (tabId && TAB_ID_TO_INDEX[tabId] != null) return TAB_ID_TO_INDEX[tabId];
    return 0;
  }

  const segment = normalizedPath.slice('/hall/'.length).split('/')[0];
  if (segment && TAB_ID_TO_INDEX[segment] != null) {
    return TAB_ID_TO_INDEX[segment];
  }

  // Nested hall pages keep a sensible parent tab highlighted.
  if (segment === 'event') {
    return TAB_ID_TO_INDEX['list-events'];
  }
  if (segment === 'detail') {
    return TAB_ID_TO_INDEX['hall-management'];
  }
  if (segment === 'vendor') {
    return TAB_ID_TO_INDEX['vendor-management'];
  }

  const queryTabId = new URLSearchParams(search).get('tab');
  if (queryTabId && TAB_ID_TO_INDEX[queryTabId] != null) {
    return TAB_ID_TO_INDEX[queryTabId];
  }
  return 0;
}

export default function HallModuleNav({
  value,
  onChange,
  accentColor = HALL_ACCENT,
  maxWidth = 1080,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const resolvedValue = useMemo(() => {
    if (typeof value === 'number' && value >= 0) return value;
    return getHallTabIndexFromLocation(location.pathname, location.search);
  }, [location.pathname, location.search, value]);

  const handleChange = (_event, nextTab) => {
    if (typeof onChange === 'function') {
      onChange(nextTab);
      return;
    }
    const tabItem = HALL_TAB_ITEMS[nextTab];
    if (!tabItem) return;
    navigate(getHallTabPath(tabItem.id));
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        mb: 0,
      }}
    >
      <Box sx={{ width: '100%', maxWidth }}>
        <NotchTabs
          value={resolvedValue}
          tabs={HALL_TAB_ITEMS}
          accentColor={accentColor}
          variant="material"
          onChange={handleChange}
        />
      </Box>
    </Box>
  );
}
